import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Video } from "lucide-react";
import Layout from "../components/Layout";
import GlassCard from "../components/GlassCard";
import { api, fileUrl } from "../lib/kk";

const TABS = [
  { key: "contact", label: "Contact" },
  { key: "puppy_waitlist", label: "Puppy Waitlist" },
  { key: "stud_waitlist", label: "Stud Waitlist" },
  { key: "payments", label: "Payments" },
  { key: "media", label: "Media" },
];

const DOGS = [
  { id: "luna", name: "Latto (Girl)" },
  { id: "lily", name: "Lily (Girl)" },
  { id: "reina", name: "Reina (Girl)" },
  { id: "luna2", name: "Luna (Girl)" },
  { id: "lilmama", name: "Lil Mama (Available)" },
  { id: "ace", name: "Ace (Stud)" },
  { id: "upcoming", name: "Upcoming Litter" },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("kk_admin") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("kk_admin"));
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [content, setContent] = useState({ video_path: null, galleries: {} });
  const [tab, setTab] = useState("contact");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const hdr = (tk) => ({ headers: { "X-Admin-Token": tk || token } });

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("kk_admin", data.token);
      setToken(data.token);
      setAuthed(true);
      loadAll(data.token);
    } catch {
      toast.error("Invalid password");
    }
  };

  const loadAll = async (tk) => {
    setLoading(true);
    try {
      const [leads, cont] = await Promise.all([
        api.get("/admin/leads", hdr(tk)),
        api.get("/content"),
      ]);
      setData(leads.data);
      setContent(cont.data);
    } catch {
      toast.error("Session expired. Please log in again.");
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshContent = async () => {
    const { data } = await api.get("/content");
    setContent(data);
  };

  const logout = () => {
    localStorage.removeItem("kk_admin");
    setAuthed(false);
    setToken("");
    setData(null);
  };

  useEffect(() => {
    if (authed) loadAll(token);
    // eslint-disable-next-line
  }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, {
      headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
    });
    return data.path;
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadFile(file);
      await api.put("/admin/content/video", { path }, hdr());
      await refreshContent();
      toast.success("Video uploaded!");
    } catch {
      toast.error("Upload failed. Try a smaller file.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeVideo = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/content/video", hdr());
      await refreshContent();
      toast.success("Video removed");
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoUpload = async (dogId, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const path = await uploadFile(f);
        await api.post(`/admin/content/gallery/${dogId}`, { path }, hdr());
      }
      await refreshContent();
      toast.success(`Added ${files.length} photo(s)`);
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (dogId, path) => {
    setBusy(true);
    try {
      await api.delete(`/admin/content/gallery/${dogId}`, { ...hdr(), data: { path } });
      await refreshContent();
    } finally {
      setBusy(false);
    }
  };

  if (!authed) {
    return (
      <Layout>
        <section className="py-28 px-6">
          <div className="max-w-sm mx-auto">
            <h1 className="kk-heading text-3xl text-center mb-6">Admin Login</h1>
            <GlassCard hover={false} className="p-8">
              <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
                <input className="kk-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
                <button className="kk-btn kk-btn-primary w-full rounded-full py-3" data-testid="admin-login-btn">Login</button>
              </form>
            </GlassCard>
          </div>
        </section>
      </Layout>
    );
  }

  const rows = data?.[tab] || [];

  return (
    <Layout>
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="kk-heading text-3xl">Admin Dashboard</h1>
            <button onClick={logout} className="kk-btn kk-btn-outline rounded-full px-6 py-2 text-sm" data-testid="admin-logout">Logout</button>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`px-5 py-2 rounded-full text-sm font-display uppercase tracking-wide font-bold transition-colors ${tab === t.key ? "bg-[#BB0000] text-white" : "bg-white/5 text-[#A8A9AD] hover:text-white"}`}
              >
                {t.label}{data && t.key !== "media" ? ` (${data[t.key]?.length || 0})` : ""}
              </button>
            ))}
          </div>

          {tab === "media" ? (
            <div className="space-y-6">
              {/* Video uploader */}
              <GlassCard hover={false} className="p-6">
                <h3 className="kk-heading text-xl mb-1 flex items-center gap-2"><Video size={20} className="text-[#BB0000]" /> Puppy Video</h3>
                <p className="text-[#999] text-sm mb-4">Upload an MP4/WebM/MOV. It plays muted on loop in the Video section. Replacing overwrites the current one.</p>
                {content.video_path && (
                  <div className="mb-4">
                    <video src={fileUrl(content.video_path)} className="w-full max-w-md rounded-lg" controls muted />
                    <button onClick={removeVideo} disabled={busy} className="kk-btn kk-btn-outline rounded-full px-5 py-2 text-xs mt-3 inline-flex items-center gap-2" data-testid="remove-video">
                      <Trash2 size={14} /> Remove Video
                    </button>
                  </div>
                )}
                <label className="kk-btn kk-btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2 cursor-pointer" data-testid="upload-video-label">
                  <Upload size={16} /> {content.video_path ? "Replace Video" : "Upload Video"}
                  <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={busy} data-testid="video-input" />
                </label>
              </GlassCard>

              {/* Dog photo uploaders */}
              <GlassCard hover={false} className="p-6">
                <h3 className="kk-heading text-xl mb-1">Dog Photo Galleries</h3>
                <p className="text-[#999] text-sm mb-5">Upload one or more photos per dog. The first photo becomes the card cover; all photos appear in the gallery.</p>
                <div className="space-y-6">
                  {DOGS.map((dog) => {
                    const photos = content.galleries?.[dog.id] || [];
                    return (
                      <div key={dog.id} className="border-t border-white/10 pt-5 first:border-t-0 first:pt-0" data-testid={`media-dog-${dog.id}`}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <p className="kk-heading text-base">{dog.name} <span className="text-[#999] text-xs font-normal">· {photos.length} photo(s)</span></p>
                          <label className="kk-btn kk-btn-outline rounded-full px-4 py-1.5 text-xs inline-flex items-center gap-2 cursor-pointer">
                            <Upload size={13} /> Add Photos
                            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoUpload(dog.id, e)} disabled={busy} data-testid={`photo-input-${dog.id}`} />
                          </label>
                        </div>
                        {photos.length > 0 && (
                          <div className="flex flex-wrap gap-3">
                            {photos.map((p) => (
                              <div key={p} className="relative">
                                <img src={fileUrl(p)} alt="" className="w-24 h-24 object-cover rounded-lg" />
                                <button onClick={() => removePhoto(dog.id, p)} className="absolute -top-2 -right-2 bg-[#BB0000] text-white rounded-full p-1" data-testid={`remove-photo-${dog.id}`}>
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            </div>
          ) : (
            <GlassCard hover={false} className="p-6 overflow-x-auto">
              {loading ? (
                <p className="text-[#999]">Loading…</p>
              ) : rows.length === 0 ? (
                <p className="text-[#999]" data-testid="admin-empty">No submissions yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="admin-table">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-white/10 align-top">
                        <td className="py-4 pr-4">
                          {Object.entries(r)
                            .filter(([k]) => !["id", "_id", "lead_type", "metadata"].includes(k))
                            .map(([k, v]) => (
                              <div key={k} className="mb-1">
                                <span className="text-[#BB0000] font-bold uppercase text-[10px] tracking-wide mr-2">{k.replace(/_/g, " ")}</span>
                                <span className="text-[#E0E0E0]">{String(v)}</span>
                              </div>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </GlassCard>
          )}
        </div>
      </section>
    </Layout>
  );
}
